import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import HistoriquePrixModal from './HistoriquePrixModal';
import './DemandesPrixManagement.css';

const API_URL = import.meta.env.VITE_API_URL ;

export const getProduitUnit = (prod) => {
    if (!prod) return 'pièce';
    if (prod.uniteMesure) {
        const u = String(prod.uniteMesure).trim().toLowerCase();
        if (['ml', 'm.l.', 'mètre linéaire', 'mètres linéaires', 'metres lineaires', 'mètre', 'm'].includes(u)) return 'ml';
        return prod.uniteMesure;
    }
    if (prod.modeGestionStock === 'lineaire') return 'ml';
    return 'pièce';
};

export const isMlUnit = (unite, modeGestionStock) => {
    const u = String(unite || '').trim().toLowerCase();
    if (['ml', 'm.l.', 'ml.', 'mètre linéaire', 'mètres linéaires', 'metres lineaires', 'mètre', 'm'].includes(u)) {
        return true;
    }
    if (modeGestionStock === 'lineaire' && (u === '' || ['ml', 'm.l.', 'mètre', 'm', 'pièce'].includes(u))) {
        return true;
    }
    return false;
};

export const isProduitLineaire = (produit) => isMlUnit(produit?.uniteMesure || produit?.unite, produit?.modeGestionStock);

export const formatStockQuantity = (quantite, isLineaire = false) => {
    const num = Number(quantite || 0);
    const formatted = num.toLocaleString('fr-FR').replace(/\s/g, '\u00A0');
    return `${formatted}${isLineaire ? '\u00A0ml' : ''}`;
};

const DemandesPrixManagement = ({ onNavigate, onLogout, currentPage = 'demandes-prix' }) => {
    // Data state
    const [demandes, setDemandes] = useState([]);
    const [fournisseursList, setFournisseursList] = useState([]);
    const [typesList, setTypesList] = useState([]);
    const [produitsList, setProduitsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // View mode: 'list' (dashboard & table) or 'create' (integrated form)
    const [viewMode, setViewMode] = useState('list');

    // List Filters state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatut, setFilterStatut] = useState('');
    const [filterFournisseur, setFilterFournisseur] = useState('');

    // History Modal control
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [historyModalTarget, setHistoryModalTarget] = useState({ produitId: null, fournisseurId: null, title: '' });

    // ── Form State for Integrated Creation View ──
    const [selectedFournisseurIds, setSelectedFournisseurIds] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedProduits, setSelectedProduits] = useState([]);
    const [dateDemande, setDateDemande] = useState(new Date().toISOString().split('T')[0]);
    const [dateLimiteReponse, setDateLimiteReponse] = useState('');
    const [remarque, setRemarque] = useState('');
    const [searchProductQuery, setSearchProductQuery] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [downloadingPdfId, setDownloadingPdfId] = useState(null);

    useEffect(() => {
        fetchInitialData();

        // Check if query params ask for direct creation mode
        const searchParams = new URLSearchParams(window.location.search);
        const action = searchParams.get('action');
        const supplierId = searchParams.get('supplierId') || searchParams.get('fournisseurId');
        const productId = searchParams.get('productId') || searchParams.get('produitId');

        if (action === 'create' || supplierId || productId) {
            setViewMode('create');
            if (supplierId) {
                setSelectedFournisseurIds([supplierId]);
            }
        }
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [resDemandes, resFourn, resTypes, resProd] = await Promise.all([
                axios.get(`${API_URL}/demandes-prix-fournisseurs`, { headers }).catch(e => ({ data: [] })),
                axios.get(`${API_URL}/fournisseurs`, { headers }).catch(e => ({ data: [] })),
                axios.get(`${API_URL}/types-produit`, { headers }).catch(e => ({ data: [] })),
                axios.get(`${API_URL}/produits`, { headers }).catch(e => ({ data: [] }))
            ]);

            setDemandes(resDemandes.data || []);
            setFournisseursList(resFourn.data || []);
            setTypesList(Array.isArray(resTypes.data) ? resTypes.data : (resTypes.data?.data || []));
            setProduitsList(Array.isArray(resProd.data) ? resProd.data : (resProd.data?.data || resProd.data?.produits || []));

            // Handle pre-selected product if specified in query params
            const searchParams = new URLSearchParams(window.location.search);
            const productId = searchParams.get('productId') || searchParams.get('produitId');
            if (productId && Array.isArray(resProd.data)) {
                const found = resProd.data.find(p => String(p._id) === String(productId));
                if (found) {
                    handleAddProduct(found);
                }
            }
        } catch (err) {
            console.error('Erreur chargement données demandes de prix:', err);
            setError('Impossible de charger la liste des demandes de prix.');
        } finally {
            setLoading(false);
        }
    };

    const fetchDemandes = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/demandes-prix-fournisseurs`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDemandes(res.data || []);
        } catch (err) {
            console.error('Erreur récupération demandes de prix:', err);
        }
    };

    const handleDeleteDemande = async (id, ref) => {
        const confirmResult = await Swal.fire({
            title: 'Suppression',
            text: `Voulez-vous vraiment supprimer la demande de prix ${ref} ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b'
        });

        if (!confirmResult.isConfirmed) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/demandes-prix-fournisseurs/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire('Supprimé', `Demande de prix ${ref} supprimée.`, 'success');
            fetchDemandes();
        } catch (err) {
            console.error('Erreur suppression:', err);
            Swal.fire('Erreur', 'Erreur lors de la suppression de la demande de prix.', 'error');
        }
    };

    // ── Téléchargement PDF de la Demande de Prix ──
    const handleDownloadPDF = async (id, reference) => {
        setDownloadingPdfId(id);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/demandes-prix-fournisseurs/${id}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const blob = new Blob([res.data], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${reference || 'demande-de-prix'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            Swal.fire({
                icon: 'success',
                title: 'PDF téléchargé',
                text: `Le PDF de la demande ${reference || ''} a été généré et téléchargé.`,
                timer: 2500,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
        } catch (err) {
            console.error('Erreur téléchargement PDF:', err);
            Swal.fire('Erreur', 'Impossible de générer ou télécharger le PDF de la demande de prix.', 'error');
        } finally {
            setDownloadingPdfId(null);
        }
    };

    // ── STEP 1: Supplier Selection Helpers ──
    const handleToggleFournisseur = (id) => {
        setSelectedFournisseurIds(prev => {
            const isSelected = prev.includes(id);
            if (isSelected) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleSelectAllFournisseurs = () => {
        const filtered = fournisseursList.filter(f => !f.typeProduit || f.typeProduit.length === 0 || f.typeProduit.some(tp => String(tp._id || tp) === String(selectedCategoryId)));
        if (selectedFournisseurIds.length === filtered.length) {
            setSelectedFournisseurIds([]);
        } else {
            const allIds = filtered.map(f => f._id);
            setSelectedFournisseurIds(allIds);
        }
    };

    // ── STEP 2: Categories Available for Selected Supplier(s) ──
    const availableCategories = useMemo(() => {
        if (selectedFournisseurIds.length === 0) return [];

        const selectedFournisseurs = fournisseursList.filter(f =>
            selectedFournisseurIds.includes(String(f._id))
        );

        // Check if any selected supplier has no explicit typeProduit set
        const hasUnrestrictedSupplier = selectedFournisseurs.some(f =>
            !f.typeProduit || !Array.isArray(f.typeProduit) || f.typeProduit.length === 0
        );

        if (hasUnrestrictedSupplier && typesList.length > 0) {
            return typesList.map(t => {
                const offeringSuppliers = selectedFournisseurs.filter(f => {
                    if (!f.typeProduit || !Array.isArray(f.typeProduit) || f.typeProduit.length === 0) return true;
                    return f.typeProduit.some(tp => String(tp._id || tp) === String(t._id));
                });
                return { ...t, offeringSuppliers };
            });
        }

        const availableTypeIds = new Set();
        selectedFournisseurs.forEach(f => {
            (f.typeProduit || []).forEach(tp => {
                const id = String(tp._id || tp);
                if (id) availableTypeIds.add(id);
            });
        });

        return typesList
            .filter(t => availableTypeIds.has(String(t._id)))
            .map(t => {
                const offeringSuppliers = selectedFournisseurs.filter(f => {
                    if (!f.typeProduit || !Array.isArray(f.typeProduit) || f.typeProduit.length === 0) return true;
                    return f.typeProduit.some(tp => String(tp._id || tp) === String(t._id));
                });
                return { ...t, offeringSuppliers };
            });
    }, [selectedFournisseurIds, fournisseursList, typesList]);

    // Reset selected category if it's no longer in availableCategories
    useEffect(() => {
        if (selectedCategoryId && availableCategories.length > 0) {
            const exists = availableCategories.some(c => String(c._id) === String(selectedCategoryId));
            if (!exists) {
                setSelectedCategoryId('');
            }
        }
    }, [availableCategories, selectedCategoryId]);

    // ── STEP 3: Products Compatible with Selected Supplier(s) & Category ──
    const compatibleProducts = useMemo(() => {
        if (selectedFournisseurIds.length === 0) return [];

        const selectedFournisseurs = fournisseursList.filter(f =>
            selectedFournisseurIds.includes(String(f._id))
        );

        return produitsList.filter(p => {
            const prodTypeId = String(p.typeProduit?._id || p.typeProduit || '');

            // Filter by category if selected
            if (selectedCategoryId && prodTypeId !== String(selectedCategoryId)) {
                return false;
            }

            // Filter by search query if typed
            if (searchProductQuery.trim() !== '') {
                const query = searchProductQuery.toLowerCase();
                const matchName = p.nomProduit && p.nomProduit.toLowerCase().includes(query);
                const matchRef = p.reference && p.reference.toLowerCase().includes(query);
                if (!matchName && !matchRef) return false;
            }

            // Filter by supplier compatibility: product MUST be offered by at least one selected supplier
            const offeringSuppliers = selectedFournisseurs.filter(f => {
                if (!f.typeProduit || !Array.isArray(f.typeProduit) || f.typeProduit.length === 0) return true;
                return f.typeProduit.some(tp => String(tp._id || tp) === prodTypeId);
            });

            return offeringSuppliers.length > 0;
        }).map(p => {
            const prodTypeId = String(p.typeProduit?._id || p.typeProduit || '');
            const offeringSuppliers = selectedFournisseurs.filter(f => {
                if (!f.typeProduit || !Array.isArray(f.typeProduit) || f.typeProduit.length === 0) return true;
                return f.typeProduit.some(tp => String(tp._id || tp) === prodTypeId);
            });
            return { ...p, offeringSuppliers };
        });
    }, [selectedFournisseurIds, selectedCategoryId, searchProductQuery, fournisseursList, produitsList]);

    // Deselection safety: Clean up any selected product no longer supplied by remaining suppliers
    useEffect(() => {
        if (selectedFournisseurIds.length === 0) {
            if (selectedProduits.length > 0) {
                setSelectedProduits([]);
            }
            return;
        }

        const selectedFournisseurs = fournisseursList.filter(f =>
            selectedFournisseurIds.includes(String(f._id))
        );

        const validProducts = selectedProduits.filter(item => {
            const prod = produitsList.find(p => String(p._id) === String(item.produitId));
            if (!prod) return false;
            const prodTypeId = String(prod.typeProduit?._id || prod.typeProduit || '');
            return selectedFournisseurs.some(f => {
                if (!f.typeProduit || !Array.isArray(f.typeProduit) || f.typeProduit.length === 0) return true;
                return f.typeProduit.some(tp => String(tp._id || tp) === prodTypeId);
            });
        });

        if (validProducts.length < selectedProduits.length) {
            setSelectedProduits(validProducts);
            Swal.fire({
                icon: 'info',
                title: 'Produits mis à jour',
                text: 'Certains produits non gérés par les fournisseurs sélectionnés ont été automatiquement retirés.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3500
            });
        }
    }, [selectedFournisseurIds, fournisseursList, produitsList]);

    // ── Product Adding & Line Management ──
    const handleAddProduct = (prod) => {
        if (selectedProduits.some(p => String(p.produitId) === String(prod._id))) {
            return;
        }

        const unitText = getProduitUnit(prod);
        const isMl = isMlUnit(unitText, prod.modeGestionStock);

        setSelectedProduits(prev => [
            ...prev,
            {
                produitId: prod._id,
                produitObj: prod,
                nomProduit: prod.nomProduit,
                reference: prod.reference,
                quantite: 1,
                unite: isMl ? 'ml' : unitText,
                remarque: '',
                modeGestionStock: prod.modeGestionStock,
                decoupes: isMl ? [{ nombrePieces: 1, longueurPieceMetre: 1 }] : []
            }
        ]);
    };

    const handleRemoveProduct = (prodId) => {
        setSelectedProduits(prev => prev.filter(p => String(p.produitId) !== String(prodId)));
    };

    const handleUpdateProductLine = (prodId, field, value) => {
        setSelectedProduits(prev => prev.map(p => {
            if (String(p.produitId) === String(prodId)) {
                return { ...p, [field]: value };
            }
            return p;
        }));
    };

    const handleUpdateDecoupe = (prodId, decoupeIndex, field, value) => {
        setSelectedProduits(prev => prev.map(p => {
            if (String(p.produitId) !== String(prodId)) return p;
            const updatedDecoupes = [...(p.decoupes || [])];
            if (!updatedDecoupes[decoupeIndex]) return p;

            const target = { ...updatedDecoupes[decoupeIndex] };
            if (field === 'nombrePieces') target.nombrePieces = Math.max(1, parseInt(value, 10) || 1);
            if (field === 'longueurPieceMetre') target.longueurPieceMetre = Math.max(0.001, Number(value) || 0.001);
            updatedDecoupes[decoupeIndex] = target;

            // Recalculate total metres
            const totalMetres = Number(updatedDecoupes.reduce((sum, d) => sum + (d.nombrePieces * d.longueurPieceMetre), 0).toFixed(3));

            return {
                ...p,
                decoupes: updatedDecoupes,
                quantite: totalMetres
            };
        }));
    };

    const handleAddDecoupe = (prodId) => {
        setSelectedProduits(prev => prev.map(p => {
            if (String(p.produitId) !== String(prodId)) return p;
            const updatedDecoupes = [...(p.decoupes || []), { nombrePieces: 1, longueurPieceMetre: 1 }];
            const totalMetres = Number(updatedDecoupes.reduce((sum, d) => sum + (d.nombrePieces * d.longueurPieceMetre), 0).toFixed(3));
            return {
                ...p,
                decoupes: updatedDecoupes,
                quantite: totalMetres
            };
        }));
    };

    const handleRemoveDecoupe = (prodId, index) => {
        setSelectedProduits(prev => prev.map(p => {
            if (String(p.produitId) !== String(prodId)) return p;
            if ((p.decoupes || []).length <= 1) return p;
            const updatedDecoupes = p.decoupes.filter((_, i) => i !== index);
            const totalMetres = Number(updatedDecoupes.reduce((sum, d) => sum + (d.nombrePieces * d.longueurPieceMetre), 0).toFixed(3));
            return {
                ...p,
                decoupes: updatedDecoupes,
                quantite: totalMetres
            };
        }));
    };

    const handleSubmitCreation = async (e) => {
        e.preventDefault();
        setFormError('');

        if (selectedFournisseurIds.length === 0) {
            setFormError('Veuillez sélectionner au moins un fournisseur cible.');
            Swal.fire('Attention', 'Veuillez sélectionner au moins un fournisseur cible.', 'warning');
            return;
        }

        if (selectedProduits.length === 0) {
            setFormError('Veuillez ajouter au moins un produit à la demande de prix.');
            Swal.fire('Attention', 'Veuillez ajouter au moins un produit à la demande de prix.', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                fournisseurs: selectedFournisseurIds,
                produits: selectedProduits.map(p => {
                    const isMl = isMlUnit(p.unite, p.modeGestionStock);
                    return {
                        produit: p.produitId,
                        quantite: Number(p.quantite) || 1,
                        unite: p.unite || (isMl ? 'ml' : 'pièce'),
                        decoupesLineaires: isMl && Array.isArray(p.decoupes) && p.decoupes.length > 0
                            ? p.decoupes.map(d => ({
                                nombrePieces: Math.max(1, parseInt(d.nombrePieces, 10) || 1),
                                longueurPieceMetre: Math.max(0.001, Number(d.longueurPieceMetre) || 0.001),
                                metres: Number((Math.max(1, parseInt(d.nombrePieces, 10) || 1) * Math.max(0.001, Number(d.longueurPieceMetre) || 0.001)).toFixed(3))
                            }))
                            : [],
                        remarque: p.remarque || ''
                    };
                }),
                dateDemande,
                dateLimiteReponse: dateLimiteReponse || null,
                remarque,
                statut: 'Brouillon'
            };

            const res = await axios.post(`${API_URL}/demandes-prix-fournisseurs`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const newDemande = res.data?.demande;
            Swal.fire({
                icon: 'success',
                title: 'Demande enregistrée',
                text: 'La demande de prix a été créée avec le statut Brouillon. Aucun email n\'a été envoyé automatiquement.',
                confirmButtonColor: '#1e3a5f'
            });

            // Reset form state
            setSelectedFournisseurIds([]);
            setSelectedCategoryId('');
            setSelectedProduits([]);
            setRemarque('');
            setDateLimiteReponse('');
            setFormError('');

            fetchDemandes();

            if (newDemande && onNavigate) {
                onNavigate('demande-prix-detail', { id: newDemande._id });
            } else {
                setViewMode('list');
            }
        } catch (err) {
            console.error('Erreur création demande de prix:', err);
            const msg = err.response?.data?.message || 'Erreur lors de la création de la demande de prix.';
            setFormError(msg);
            Swal.fire('Erreur', msg, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Filtered demands for List view
    const filteredDemandes = demandes.filter(item => {
        const matchesSearch = !searchTerm || (
            (item.reference && item.reference.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (item.remarque && item.remarque.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const matchesStatut = !filterStatut || item.statut === filterStatut;

        const matchesFournisseur = !filterFournisseur || (
            item.fournisseurs && item.fournisseurs.some(f => String(f._id || f) === String(filterFournisseur))
        );

        return matchesSearch && matchesStatut && matchesFournisseur;
    });

    // KPI Counters
    const totalDemandes = demandes.length;
    const countEnAttente = demandes.filter(d => d.statut === 'Envoyée' || d.statut === 'En attente de réponse').length;
    const countReponsesRecues = demandes.filter(d => d.statut === 'Réponse reçue' || d.statut === 'Partiellement répondue').length;
    const countTerminees = demandes.filter(d => d.statut === 'Terminée').length;

    const getStatusBadge = (statut) => {
        const classMap = {
            'Brouillon': 'statut-brouillon',
            'Envoyée': 'statut-envoyee',
            'En attente de réponse': 'statut-attente',
            'Réponse reçue': 'statut-recue',
            'Partiellement répondue': 'statut-partiel',
            'Terminée': 'statut-terminee',
            'Annulée': 'statut-annulee'
        };
        return <span className={`dp-status-badge ${classMap[statut] || ''}`}>{statut}</span>;
    };

    return (
        <div className="backoffice-layout">
            <BackOfficeMenu currentPage={currentPage} onNavigate={onNavigate} onLogout={onLogout} />

            <div className="backoffice-content">
                {/* ════════════════════════════════════════════════════════════
                   VIEW MODE 1: LISTE DES DEMANDES DE PRIX (DASHBOARD & TABLE)
                ════════════════════════════════════════════════════════════ */}
                {viewMode === 'list' && (
                    <>
                        {/* Header Title & Actions */}
                        <div className="dp-dashboard-header">
                            <div>
                                <h2>
                                    Gestion des Demandes de Prix Fournisseur
                                </h2>
                                <p className="header-subtitle">
                                    Demandez des devis multi-produits auprès de plusieurs fournisseurs et comparez les offres reçues.
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    className="btn-history-global"
                                    onClick={() => {
                                        setHistoryModalTarget({ produitId: null, fournisseurId: null, title: 'Historique des Prix Global' });
                                        setIsHistoryModalOpen(true);
                                    }}
                                >
                                    <i className="fas fa-chart-line"></i> Historique Prix
                                </button>
                                <button className="btn-create-dp" onClick={() => setViewMode('create')}>
                                    <i className="fas fa-plus-circle"></i> Nouvelle Demande
                                </button>
                            </div>
                        </div>

                        {/* KPI Stat Cards */}
                        <div className="dp-stats-cards">
                            <div className="stat-card">
                                <div className="stat-icon icon-blue"><i className="fas fa-paper-plane"></i></div>
                                <div className="stat-info">
                                    <span className="stat-value">{totalDemandes}</span>
                                    <span className="stat-label">Total Demandes</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon icon-orange"><i className="fas fa-hourglass-half"></i></div>
                                <div className="stat-info">
                                    <span className="stat-value">{countEnAttente}</span>
                                    <span className="stat-label">En Attente de Réponse</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon icon-green"><i className="fas fa-reply-all"></i></div>
                                <div className="stat-info">
                                    <span className="stat-value">{countReponsesRecues}</span>
                                    <span className="stat-label">Réponses Reçues</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon icon-purple"><i className="fas fa-check-double"></i></div>
                                <div className="stat-info">
                                    <span className="stat-value">{countTerminees}</span>
                                    <span className="stat-label">Offres Retenues & Converties</span>
                                </div>
                            </div>
                        </div>

                        {/* Search & Filters */}
                        <div className="dp-filters-bar">
                            <div className="search-input-wrap">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Rechercher par référence ou remarque..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="filter-select-wrap">
                                <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}>
                                    <option value="">-- Tous les statuts --</option>
                                    <option value="Brouillon">Brouillon</option>
                                    <option value="Envoyée">Envoyée</option>
                                    <option value="En attente de réponse">En attente de réponse</option>
                                    <option value="Réponse reçue">Réponse reçue</option>
                                    <option value="Partiellement répondue">Partiellement répondue</option>
                                    <option value="Terminée">Terminée</option>
                                    <option value="Annulée">Annulée</option>
                                </select>
                            </div>

                            <div className="filter-select-wrap">
                                <select value={filterFournisseur} onChange={(e) => setFilterFournisseur(e.target.value)}>
                                    <option value="">-- Tous les fournisseurs --</option>
                                    {fournisseursList.map(f => (
                                        <option key={f._id} value={f._id}>{f.nom || f.raisonSociale}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Main Table */}
                        <div className="dp-table-card">
                            {loading ? (
                                <div className="dp-loading-spinner">
                                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                                    <p>Chargement des demandes de prix...</p>
                                </div>
                            ) : error ? (
                                <div className="dp-alert dp-alert-error">{error}</div>
                            ) : filteredDemandes.length === 0 ? (
                                <div className="dp-empty-state">
                                    <i className="fas fa-inbox" style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '14px' }}></i>
                                    <p>Aucune demande de prix ne correspond aux filtres.</p>
                                </div>
                            ) : (
                                <table className="dp-table">
                                    <thead>
                                        <tr>
                                            <th>Référence</th>
                                            <th>Date</th>
                                            <th>Fournisseurs Sollicités</th>
                                            <th>Nb Produits</th>
                                            <th>Statut</th>
                                            <th>Réponses</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDemandes.map((item) => {
                                            const nbReponse = item.reponses ? item.reponses.length : 0;
                                            const numUniqueSuppliersResponded = item.reponses
                                                ? new Set(item.reponses.map(r => String(r.fournisseur?._id || r.fournisseur))).size
                                                : 0;

                                            return (
                                                <tr key={item._id}>
                                                    <td>
                                                        <span
                                                            className="ref-link"
                                                            onClick={() => {
                                                                if (onNavigate) {
                                                                    onNavigate('demande-prix-detail', { id: item._id });
                                                                }
                                                            }}
                                                        >
                                                            {item.reference}
                                                        </span>
                                                    </td>
                                                    <td>{new Date(item.dateDemande).toLocaleDateString('fr-FR')}</td>
                                                    <td>
                                                        <div className="suppliers-tags-cell">
                                                            {(item.fournisseurs || []).map((f, fIdx) => (
                                                                <span key={fIdx} className="supplier-tag">
                                                                    {f.nom || f.raisonSociale || 'Fournisseur'}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="products-count-badge">
                                                            {item.produits?.length || 0} produit(s)
                                                        </span>
                                                    </td>
                                                    <td>{getStatusBadge(item.statut)}</td>
                                                    <td>
                                                        <span className={`reponses-count-pill ${nbReponse > 0 ? 'active' : ''}`}>
                                                            <i className="fas fa-comments"></i> {nbReponse} version(s) ({numUniqueSuppliersResponded}/{item.fournisseurs?.length || 0})
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div className="table-actions-cell">
                                                            <button
                                                                className="action-btn pdf-btn"
                                                                title="Télécharger le PDF de la demande"
                                                                onClick={() => handleDownloadPDF(item._id, item.reference)}
                                                                disabled={downloadingPdfId === item._id}
                                                            >
                                                                {downloadingPdfId === item._id ? (
                                                                    <><i className="fas fa-spinner fa-spin"></i> PDF</>
                                                                ) : (
                                                                    <><i className="fas fa-file-pdf"></i> PDF</>
                                                                )}
                                                            </button>
                                                            <button
                                                                className="action-btn view-btn"
                                                                title="Consulter et comparer"
                                                                onClick={() => {
                                                                    if (onNavigate) {
                                                                        onNavigate('demande-prix-detail', { id: item._id });
                                                                    }
                                                                }}
                                                            >
                                                                <i className="fas fa-eye"></i> Détails
                                                            </button>
                                                            <button
                                                                className="action-btn delete-btn"
                                                                title="Supprimer la demande"
                                                                onClick={() => handleDeleteDemande(item._id, item.reference)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}

                {/* ════════════════════════════════════════════════════════════
                   VIEW MODE 2: FORMULAIRE INTEGRÉ DE CRÉATION
                   Directement intégré dans la page principale BackOffice
                ════════════════════════════════════════════════════════════ */}
                {viewMode === 'create' && (
                    <div className="dp-integrated-form-page">
                        {/* Integrated Page Header Toolbar */}
                        <div className="fbf-toolbar">
                            <button type="button" className="fbf-back-btn" onClick={() => setViewMode('list')}>
                                <i className="fas fa-arrow-left"></i>
                                Retour à la liste
                            </button>
                            <div>
                                <span className="fbf-kicker">Achats & Fournisseurs</span>
                                <h1>Nouvelle Demande de Prix Fournisseur</h1>
                                <p>Formulaire intégré pour demander des offres tarifaires multi-produits à un ou plusieurs fournisseurs.</p>
                            </div>
                        </div>

                        {formError && (
                            <div className="fbf-alert warning" style={{ marginBottom: '20px' }}>
                                <i className="fas fa-exclamation-triangle"></i>
                                <span>{formError}</span>
                            </div>
                        )}

                        <div className="dp-creation-notice-box">
                            <i className="fas fa-shield-alt"></i>
                            <div>
                                <strong>Enregistrement en base de données sans envoi d'email</strong>
                                <p>La création d'une demande de prix l'enregistre avec le statut <b>Brouillon</b>. Aucun email n'est envoyé automatiquement aux fournisseurs. L'envoi par email reste une action manuelle distincte réalisable depuis la fiche détaillée de la demande.</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmitCreation} className="fbf-shell">
                            <section className="fbf-main">

                                {/* ── ÉTAPE 1: Sélection de la catégorie produit ── */}
                                <div className="fbf-section fbf-workflow">
                                    <div className="fbf-section-title">
                                        <span>Étape 1</span>
                                        <strong>Sélection de la Catégorie Produit *</strong>
                                    </div>
                                    <div className="dp-step-intro">
                                        <p style={{ margin: '0 0 14px 0', color: '#64748b', fontSize: '0.9rem' }}>
                                            Choisissez d'abord la catégorie de produit souhaitée pour filtrer les fournisseurs rattachés et les produits correspondants.
                                        </p>
                                    </div>

                                    <div className="fbf-grid-2">
                                        <label className="fbf-field">
                                            <span>Catégorie TypeProduit *</span>
                                            <select
                                                value={selectedCategoryId}
                                                onChange={(e) => {
                                                    setSelectedCategoryId(e.target.value);
                                                    setSelectedFournisseurIds([]);
                                                    setSelectedProduits([]);
                                                }}
                                                required
                                            >
                                                <option value="">-- Sélectionner une catégorie de produit --</option>
                                                {typesList.map(cat => (
                                                    <option key={cat._id} value={cat._id}>
                                                        {cat.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        {selectedCategoryId && (
                                            <div className="dp-category-stats-pill">
                                                <div style={{ color: '#0284c7', fontWeight: 700 }}>
                                                    <i className="fas fa-layer-group" style={{ marginRight: '6px' }}></i>
                                                    Catégorie sélectionnée: {typesList.find(t => String(t._id) === String(selectedCategoryId))?.label}
                                                </div>
                                                <small style={{ color: '#64748b' }}>
                                                    {availableCategories.length > 0 ? `${compatibleProducts.length} produit(s) disponible(s)` : 'Chargement...'}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* ── ÉTAPE 2: Sélection des Fournisseurs rattachés ── */}
                                <div className="fbf-section">
                                    <div className="fbf-section-title">
                                        <span>Étape 2</span>
                                        <strong>Fournisseurs rattachés à cette catégorie * ({selectedFournisseurIds.length} sélectionné(s))</strong>
                                    </div>

                                    {!selectedCategoryId ? (
                                        <div className="fbf-alert warning">
                                            <i className="fas fa-lock"></i>
                                            <span>Veuillez sélectionner une catégorie à l'Étape 1 pour afficher les fournisseurs compatibles.</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="dp-step-intro">
                                                <button
                                                    type="button"
                                                    className="fbf-mini-btn"
                                                    style={{ marginBottom: '14px' }}
                                                    onClick={handleSelectAllFournisseurs}
                                                >
                                                    <i className="fas fa-check-double"></i>
                                                    {selectedFournisseurIds.length === fournisseursList.filter(f => !f.typeProduit || f.typeProduit.length === 0 || f.typeProduit.some(tp => String(tp._id || tp) === String(selectedCategoryId))).length ? 'Tout désélectionner' : 'Sélectionner tous les fournisseurs de la catégorie'}
                                                </button>
                                            </div>

                                            <div className="suppliers-checkbox-grid">
                                                {fournisseursList.filter(f => !f.typeProduit || f.typeProduit.length === 0 || f.typeProduit.some(tp => String(tp._id || tp) === String(selectedCategoryId))).length === 0 ? (
                                                    <div className="fbf-empty">
                                                        <i className="fas fa-building"></i>
                                                        <span>Aucun fournisseur rattaché à cette catégorie.</span>
                                                    </div>
                                                ) : (
                                                    fournisseursList.filter(f => !f.typeProduit || f.typeProduit.length === 0 || f.typeProduit.some(tp => String(tp._id || tp) === String(selectedCategoryId))).map(f => {
                                                        const isChecked = selectedFournisseurIds.includes(f._id);
                                                        const numTypes = Array.isArray(f.typeProduit) ? f.typeProduit.length : 0;
                                                        const hasEmail = Boolean(f.email && f.email.trim());

                                                        return (
                                                            <div
                                                                key={f._id}
                                                                className={`supplier-chip ${isChecked ? 'selected' : ''}`}
                                                                onClick={() => handleToggleFournisseur(f._id)}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={() => { }}
                                                                    />
                                                                    <div className="supplier-chip-info">
                                                                        <strong>{f.nom || f.raisonSociale}</strong>
                                                                        <small>Code: {f.code || 'N/A'} {numTypes > 0 ? `· ${numTypes} cat.` : ''}</small>
                                                                    </div>
                                                                </div>

                                                                <div className="supplier-chip-contact-badge">
                                                                    {hasEmail ? (
                                                                        <span className="supplier-email-hint" title={f.email}>
                                                                            <i className="fas fa-envelope"></i> Email dispo
                                                                        </span>
                                                                    ) : (
                                                                        <span className="supplier-no-email-hint">
                                                                            <i className="fas fa-exclamation-circle"></i> Sans email
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* ── ÉTAPE 3: Sélection des Produits & Quantités ── */}
                                <div className="fbf-section">
                                    <div className="fbf-section-title">
                                        <span>Étape 3</span>
                                        <strong>Sélection des Produits et Quantités * ({selectedProduits.length} produit(s) ajouté(s))</strong>
                                    </div>

                                    {!selectedCategoryId ? (
                                        <div className="fbf-empty">
                                            <i className="fas fa-boxes"></i>
                                            <span>Sélectionnez une catégorie à l'Étape 1.</span>
                                        </div>
                                    ) : selectedFournisseurIds.length === 0 ? (
                                        <div className="fbf-empty">
                                            <i className="fas fa-truck"></i>
                                            <span>Sélectionnez au moins un fournisseur à l'Étape 2 pour charger la liste des produits.</span>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Product Search Input */}
                                            <div className="search-input-wrap" style={{ marginBottom: '16px' }}>
                                                <i className="fas fa-search"></i>
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher un produit par nom ou référence pour l'ajouter..."
                                                    value={searchProductQuery}
                                                    onChange={(e) => setSearchProductQuery(e.target.value)}
                                                />
                                            </div>

                                            {/* Compatible Products Grid / Catalog */}
                                            <div className="dp-products-picker-grid">
                                                {compatibleProducts.length === 0 ? (
                                                    <div className="fbf-empty" style={{ gridColumn: '1 / -1' }}>
                                                        <i className="fas fa-info-circle"></i>
                                                        <span>Aucun produit compatible trouvé pour cette catégorie / ces fournisseurs.</span>
                                                    </div>
                                                ) : (
                                                    compatibleProducts.slice(0, 12).map(prod => {
                                                        const isSelected = selectedProduits.some(p => String(p.produitId) === String(prod._id));
                                                        const lineaire = isProduitLineaire(prod);
                                                        const supplierTagList = (prod.offeringSuppliers || []).map(s => s.nom || s.raisonSociale);

                                                        return (
                                                            <div
                                                                key={prod._id}
                                                                className={`dp-product-picker-card ${isSelected ? 'selected' : ''}`}
                                                                onClick={() => handleAddProduct(prod)}
                                                            >
                                                                <div className="dp-product-picker-head">
                                                                    <strong>{prod.nomProduit}</strong>
                                                                    <code>Réf: {prod.reference}</code>
                                                                </div>
                                                                <div className="dp-product-picker-meta">
                                                                    <span className="fbf-stock-badge">
                                                                        Stock: {formatStockQuantity(prod.quantite, lineaire)}
                                                                    </span>
                                                                    {prod.typeProduit?.label && (
                                                                        <span className="dp-cat-tag">
                                                                            {prod.typeProduit.label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {/* Display supplier compatibility tag */}
                                                                <div className="dp-supplier-offered-tags">
                                                                    <i className="fas fa-truck" style={{ fontSize: '0.75rem', color: '#0284c7' }}></i>
                                                                    <span>Fournisseur(s): {supplierTagList.join(', ')}</span>
                                                                </div>
                                                                <button type="button" className={`dp-add-btn ${isSelected ? 'added' : ''}`}>
                                                                    <i className={`fas ${isSelected ? 'fa-check' : 'fa-plus'}`}></i>
                                                                    {isSelected ? 'Ajouté' : 'Ajouter'}
                                                                </button>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>

                                            {/* Selected Products Table */}
                                            <div style={{ marginTop: '24px' }}>
                                                <h4 style={{ margin: '0 0 12px 0', color: '#1e3a5f', fontSize: '1rem', fontWeight: 700 }}>
                                                    <i className="fas fa-list-check" style={{ color: '#0284c7', marginRight: '8px' }}></i>
                                                    Tableau des produits sélectionnés pour la demande de prix
                                                </h4>

                                                {selectedProduits.length === 0 ? (
                                                    <div className="fbf-empty">
                                                        <i className="fas fa-shopping-basket"></i>
                                                        <span>Aucun produit sélectionné. Cliquez sur les produits ci-dessus pour les ajouter à la demande.</span>
                                                    </div>
                                                ) : (
                                                    <div className="fbf-products-list">
                                                        {selectedProduits.map((item) => {
                                                            const isMl = isMlUnit(item.unite, item.modeGestionStock);
                                                            return (
                                                                <article key={item.produitId} className="fbf-product-row selected">
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                                                        <div>
                                                                            <strong style={{ color: '#1e3a5f', fontSize: '1rem' }}>{item.nomProduit}</strong>
                                                                            <small style={{ color: '#64748b', display: 'block', marginTop: '2px' }}>
                                                                                Réf: {item.reference} · Unité: {item.unite || 'pièce'} {isMl ? '· Découpage linéaire (ml)' : ''}
                                                                            </small>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="fbf-icon-danger"
                                                                            onClick={() => handleRemoveProduct(item.produitId)}
                                                                            title="Supprimer ce produit"
                                                                        >
                                                                            <i className="fas fa-trash"></i>
                                                                        </button>
                                                                    </div>

                                                                    <div className="fbf-product-controls">
                                                                        {isMl ? (
                                                                            <div className="fbf-decoupes">
                                                                                {(item.decoupes || []).map((decoupe, idx) => (
                                                                                    <div className="fbf-decoupe-row" key={`${item.produitId}-${idx}`}>
                                                                                        <label>
                                                                                            Pièces
                                                                                            <input
                                                                                                type="number"
                                                                                                min="1"
                                                                                                value={decoupe.nombrePieces}
                                                                                                onChange={(e) => handleUpdateDecoupe(item.produitId, idx, 'nombrePieces', e.target.value)}
                                                                                            />
                                                                                        </label>
                                                                                        <label>
                                                                                            Longueur/p (m)
                                                                                            <input
                                                                                                type="number"
                                                                                                min="0.001"
                                                                                                step="0.001"
                                                                                                value={decoupe.longueurPieceMetre}
                                                                                                onChange={(e) => handleUpdateDecoupe(item.produitId, idx, 'longueurPieceMetre', e.target.value)}
                                                                                            />
                                                                                        </label>
                                                                                        <strong style={{ alignSelf: 'center', marginTop: '16px' }}>
                                                                                            {(decoupe.nombrePieces * decoupe.longueurPieceMetre).toFixed(3)} m
                                                                                        </strong>
                                                                                        {item.decoupes.length > 1 && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="fbf-icon-danger"
                                                                                                onClick={() => handleRemoveDecoupe(item.produitId, idx)}
                                                                                            >
                                                                                                <i className="fas fa-times"></i>
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                ))}
                                                                                <button type="button" className="fbf-mini-btn" onClick={() => handleAddDecoupe(item.produitId)}>
                                                                                    <i className="fas fa-plus"></i> Ajouter une découpe
                                                                                </button>
                                                                                <div className="fbf-line-total" style={{ marginTop: '6px' }}>
                                                                                    Quantité totale estimée: <strong>{Number(item.quantite).toLocaleString('fr-FR')} m</strong>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="fbf-grid-2">
                                                                                <label className="fbf-field">
                                                                                    <span>Quantité demandée *</span>
                                                                                    <input
                                                                                        type="number"
                                                                                        step="any"
                                                                                        min="0.001"
                                                                                        value={item.quantite}
                                                                                        onChange={(e) => handleUpdateProductLine(item.produitId, 'quantite', e.target.value)}
                                                                                        required
                                                                                    />
                                                                                </label>
                                                                                <label className="fbf-field">
                                                                                    <span>Unité Produit</span>
                                                                                    <div className="dp-unit-badge" style={{ marginTop: '4px' }}>
                                                                                        {item.unite || 'pièce'}
                                                                                    </div>
                                                                                </label>
                                                                            </div>
                                                                        )}

                                                                        <label className="fbf-field" style={{ marginTop: '12px' }}>
                                                                            <span>Remarque / Spécifications particulières</span>
                                                                            <input
                                                                                type="text"
                                                                                placeholder="Spécification technique, variante souhaitée..."
                                                                                value={item.remarque}
                                                                                onChange={(e) => handleUpdateProductLine(item.produitId, 'remarque', e.target.value)}
                                                                            />
                                                                        </label>
                                                                    </div>
                                                                </article>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* ── ÉTAPE 4: Dates & Instructions ── */}
                                <div className="fbf-section">
                                    <div className="fbf-section-title">
                                        <span>Détails</span>
                                        <strong>Dates et instructions de la demande</strong>
                                    </div>
                                    <div className="fbf-grid-2">
                                        <label className="fbf-field">
                                            <span>Date de demande *</span>
                                            <input
                                                type="date"
                                                value={dateDemande}
                                                onChange={(e) => setDateDemande(e.target.value)}
                                                required
                                            />
                                        </label>
                                        <label className="fbf-field">
                                            <span>Date limite de réponse (facultatif)</span>
                                            <input
                                                type="date"
                                                value={dateLimiteReponse}
                                                onChange={(e) => setDateLimiteReponse(e.target.value)}
                                            />
                                        </label>
                                    </div>
                                    <div className="fbf-grid-2" style={{ marginTop: '16px' }}>
                                        <div className="fbf-field">
                                            <span>Statut initial</span>
                                            <div className="fbf-static-badge statut-brouillon">
                                                <i className="fas fa-file-alt" style={{ marginRight: '6px' }}></i>
                                                Brouillon (Enregistré sans envoi automatique)
                                            </div>
                                        </div>
                                        <label className="fbf-field">
                                            <span>Remarques / Consignes pour les fournisseurs</span>
                                            <textarea
                                                rows="2"
                                                placeholder="Ex: Demande d'offre tarifaire globale pour projet Sastra, avec frais de livraison inclus..."
                                                value={remarque}
                                                onChange={(e) => setRemarque(e.target.value)}
                                            />
                                        </label>
                                    </div>
                                </div>

                            </section>

                            {/* ── SIDEBAR SUMMARY ── */}
                            <aside className="fbf-summary">
                                <div className="fbf-summary-card">
                                    <span className="fbf-kicker">Résumé</span>
                                    <h2>Demande de Prix</h2>
                                    <dl>
                                        <div>
                                            <dt>Fournisseurs sollicités</dt>
                                            <dd>{selectedFournisseurIds.length}</dd>
                                        </div>
                                        <div>
                                            <dt>Catégories concernées</dt>
                                            <dd>{availableCategories.length}</dd>
                                        </div>
                                        <div>
                                            <dt>Produits sélectionnés</dt>
                                            <dd>{selectedProduits.length}</dd>
                                        </div>
                                        <div>
                                            <dt>Statut initial</dt>
                                            <dd><span className="dp-status-badge statut-brouillon">Brouillon</span></dd>
                                        </div>
                                    </dl>

                                    <div className="fbf-actions">
                                        <button
                                            type="button"
                                            className="fbf-secondary-btn"
                                            onClick={() => setViewMode('list')}
                                            disabled={submitting}
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            type="submit"
                                            className="fbf-primary-btn"
                                            disabled={submitting || selectedFournisseurIds.length === 0 || selectedProduits.length === 0}
                                        >
                                            <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                                            {submitting ? 'Enregistrement...' : 'Enregistrer la demande (Brouillon)'}
                                        </button>
                                    </div>
                                </div>
                            </aside>
                        </form>
                    </div>
                )}

                {/* Global History Modal */}
                <HistoriquePrixModal
                    isOpen={isHistoryModalOpen}
                    onClose={() => setIsHistoryModalOpen(false)}
                    produitId={historyModalTarget.produitId}
                    fournisseurId={historyModalTarget.fournisseurId}
                    title={historyModalTarget.title}
                />
            </div>
        </div>
    );
};

export default DemandesPrixManagement;

